import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import useSmartQueryClient from "./useSmartQueryClient";
import { UserRole, useUserSettings } from "./useUserSettings";

export interface User {
  id: number;
  name: string;
  email: string;
  username: string;
  company_id: number;
  role: UserRole;
}

export interface Company {
  id: number;
  name: string;
  type: "production" | "test" | "trial";
}

export interface PatchUser {
  name?: string;
  email?: string;
  username?: string;
  company_id?: number;
}

export interface CreateCompany {
  type: "production" | "test" | "trial";
  name: string;
  access: string[];
}

export type PatchCompany = Partial<CreateCompany>;

export function useGetUsers() {
  const query = useQuery({
    queryKey: ["users"],
    queryFn: () => axios.get<User[]>("admin/users").then((data) => data.data),
  });

  return query;
}

export function useGetUser({ userId }: { userId: number }) {
  const query = useQuery({
    queryKey: ["users", userId],
    queryFn: () => axios.get<User>("admin/users/" + userId).then((data) => data.data),
  });

  return query;
}

export function useGetCompany({ companyId, enabled }: { companyId: number; enabled?: boolean }) {
  const query = useQuery({
    queryKey: ["companies", companyId],
    enabled,
    queryFn: () => axios.get<Company>("admin/companies/" + companyId).then((data) => data.data),
  });

  return query;
}

export function useGetCompanies() {
  const query = useQuery({
    queryKey: ["companies"],
    queryFn: () => axios.get<User[]>("admin/companies").then((data) => data.data),
  });

  return query;
}

export function usePatchUser() {
  const smartQueryClient = useSmartQueryClient();
  const userSettings = useUserSettings();

  const query = useMutation({
    mutationKey: ["user", "patch"],
    onSuccess: (_, { userId }) => {
      if (userId === userSettings.data?.user_id) {
        smartQueryClient.invalidateQueries();
      } else {
        smartQueryClient.invalidateQueries(["users"]);
      }
    },
    mutationFn: ({ userId, user }: { userId: number; user: PatchUser }) =>
      axios.patch(`admin/users/${userId}`, user),
  });

  return query;
}

export function usePatchCompany() {
  const smartQueryClient = useSmartQueryClient();
  const userSettings = useUserSettings();

  const query = useMutation({
    mutationKey: ["company", "patch"],
    onSuccess: (_, { companyId }) => {
      if (companyId === userSettings.data?.company_id) {
        smartQueryClient.invalidateQueries();
      } else {
        smartQueryClient.invalidateQueries(["companies"]);
      }
    },
    mutationFn: ({ companyId, company }: { companyId: number; company: PatchCompany }) =>
      axios.patch(`admin/companies/${companyId}`, company),
  });

  return query;
}

export function useCreateCompany() {
  const smartQueryClient = useSmartQueryClient();
  const query = useMutation({
    mutationKey: ["company", "create"],
    mutationFn: ({ company }: { company: CreateCompany }) => axios.post(`admin/companies`, company),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["companies"]);
    },
  });

  return query;
}
